import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TariffType } from '@shared/models/visit.model';

interface SubscriptionTariffDto {
  type: string;
}

interface DoctorSubscriptionDto {
  id: string;
  tariff: SubscriptionTariffDto;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DoctorTariffService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  readonly visitsEnabled = signal<boolean>(false);
  readonly tariffType = signal<TariffType | null>(null);
  readonly loaded = signal<boolean>(false);
  /**
   * Запрос тарифа завершился ошибкой. Отличает «не смогли узнать условия»
   * от штатного «у врача нет подписки» — в обоих случаях tariffType() === null,
   * но пациенту это надо объяснять по-разному.
   */
  readonly loadFailed = signal<boolean>(false);

  readonly isPrepaid = computed(() => this.tariffType() === 'PREPAID');
  readonly isCommission = computed(() => this.tariffType() === 'COMMISSION');

  private _loaded$ = new ReplaySubject<void>(1);
  loaded$ = this._loaded$.asObservable();
  private loading = false;

  loadTariff(): void {
    if (this.loaded() || this.loading) return;
    this.loading = true;

    this.http.get<DoctorSubscriptionDto | null>(
      `${this.apiUrl}/patient/slots/doctor-subscription`
    ).subscribe({
      next: (subscription) => {
        this.visitsEnabled.set(!!subscription?.isActive);
        this.tariffType.set((subscription?.tariff?.type as TariffType) ?? null);
        this.loadFailed.set(false);
        this.loaded.set(true);
        this._loaded$.next();
      },
      error: () => {
        this.visitsEnabled.set(false);
        this.tariffType.set(null);
        this.loadFailed.set(true);
        this.loaded.set(true);
        this._loaded$.next();
      }
    });
  }

  /**
   * Гарантирует загрузку тарифа врача и сообщает о её завершении.
   * Нужен там, где логика зависит от типа тарифа: до завершения загрузки
   * tariffType() === null, и вызывающий код выбрал бы стратегию наугад.
   */
  ensureLoaded(): Observable<void> {
    this.loadTariff();
    return this.loaded$.pipe(take(1));
  }

  /**
   * Полный сброс состояния (на logout). После reset() следующий loadTariff()
   * выполнится заново — без этого новый пациент видит тариф предыдущего.
   */
  reset(): void {
    this.visitsEnabled.set(false);
    this.tariffType.set(null);
    this.loadFailed.set(false);
    this.loaded.set(false);
    this.loading = false;
    this._loaded$ = new ReplaySubject<void>(1);
    this.loaded$ = this._loaded$.asObservable();
  }
}
